import { Delete16Regular } from "@fluentui/react-icons";
import type { AnalysisSectionContentProps } from "./AnalysisSectionContent";
import { HorizontalScrollArea } from "./HorizontalScrollArea";
import { SampleAnalysisContent } from "./SampleAnalysisContent";
import { DecimalInput } from "../../../DecimalInput";

export function XrfAnalysisContent(props: AnalysisSectionContentProps) {
  const { section, normalizedSamples, onUpdateSectionRowCell, onRemoveXrfElement, onAddXrfElement, readOnly = false } = props;
  const emptyColumns = Array.from({ length: Math.max(0, 6 - normalizedSamples.length) }, (_, index) => index);
  const grid = (
    <HorizontalScrollArea scrollAreaId="analysis-result-xrf-grid">
      <table style={{ minWidth: Math.max(700, 128 + normalizedSamples.length * 177) }} className="w-full table-fixed border-collapse bg-primary-dark/5 dark:bg-black/20 text-center text-[14px] text-primary-dark dark:text-primary-light [&_th]:border-2 [&_th]:border-primary-dark/10 dark:[&_th]:border-oi-dark-background [&_th]:font-normal [&_td]:border-2 [&_td]:border-primary-dark/10 dark:[&_td]:border-oi-dark-background">
        <colgroup>
          <col className="w-[128px]" />
          {normalizedSamples.map(sample => <col key={sample.sampleId} className="w-[15.8%]" />)}
          {emptyColumns.map(index => <col key={index} className={index === emptyColumns.length - 1 ? "w-[9.6%]" : "w-[15.8%]"} />)}
        </colgroup>
        <thead className="h-[50px] bg-primary-dark/5 dark:bg-black/20">
          <tr>
            <th>Element</th>
            {normalizedSamples.map(sample => <th key={sample.sampleId} className="whitespace-nowrap px-2">{sample.sampleId}</th>)}
            {emptyColumns.map(index => <th key={index} aria-hidden="true" />)}
          </tr>
        </thead>
        <tbody>
          {section.rows.map((row, rowIndex) => (
            <tr key={row.rowLabel} className="h-[37px]">
              <td>
                <div className="flex items-center gap-5 px-1">
                  {!readOnly && <button type="button" aria-label={`Remove ${row.rowLabel}`} title={`Remove ${row.rowLabel}`} onClick={() => onRemoveXrfElement(row.rowLabel)} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary-dark/35 dark:border-primary-light/35 bg-primary-dark/20 dark:bg-primary-light/20 text-primary-dark/90 dark:text-primary-light/90 hover:bg-primary-dark/30 dark:hover:bg-primary-light/30 focus-visible:outline focus-visible:outline-oi-button-blue"><Delete16Regular /></button>}
                  <span>{row.rowLabel}</span>
                </div>
              </td>
              {normalizedSamples.map(sample => (
                <td key={sample.sampleId}>
                  <DecimalInput disabled={readOnly} aria-label={`${sample.sampleId} ${row.rowLabel}`} className="h-[30px] w-[76px] max-w-[calc(100%_-_12px)] border border-primary-dark/50 dark:border-primary-light/50 bg-black/0 px-2 text-center text-[14px] font-bold text-oi-button-blue dark:text-oi-powder-form-yellow outline-none placeholder:text-oi-button-blue dark:placeholder:text-oi-powder-form-yellow focus:border-oi-button-blue dark:focus:border-oi-powder-form-yellow" placeholder="-" value={row.sampleValues[sample.sampleId] ?? ""} onValueChange={value => onUpdateSectionRowCell(section.analysisKey, rowIndex, sample.sampleId, value)} />
                </td>
              ))}
              {emptyColumns.map(index => <td key={index} aria-hidden="true" />)}
            </tr>
          ))}
          {!readOnly && <tr className="h-[36px]">
            <td>
              <button type="button" onClick={onAddXrfElement} className="flex items-center gap-2 px-1 text-[12px]">
                <span aria-hidden="true" className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-primary-light text-[22px] leading-none text-primary-dark dark:text-oi-dark-background">+</span>
                <span className="whitespace-nowrap border border-primary-dark/50 dark:border-primary-light/50 px-1 py-1">Add element</span>
              </button>
            </td>
            {normalizedSamples.map(sample => <td key={sample.sampleId} aria-hidden="true" />)}
            {emptyColumns.map(index => <td key={index} aria-hidden="true" />)}
          </tr>}
        </tbody>
      </table>
    </HorizontalScrollArea>
  );

  return <SampleAnalysisContent {...props} grid={grid} />;
}
